import {Injectable} from "@angular/core";
import {FormService} from "../../../../umbrella-common/services/form.service";
import {deviceType} from "../../../../app.component";
import {TranslateService} from "@ngx-translate/core";
import moment from "moment/moment";
import {dateWithoutTime, isInPast} from "../../../../umbrella-common/date-functions";
import {AdditionalInitialCertificationQuestions, Order, ProcessType, Product, StandardName} from "../../../../store/state/order.state";
import {ValidationMsg} from "../../../../umbrella-common/services/standard-error-map";
import {FormBuilder, FormControl, FormGroup, ValidationErrors, Validators} from "@angular/forms";
import {Store} from "@ngrx/store";
import {AppState} from "../../../../store/state/app.state";
import {InitialCertificationDatesValidators} from "./initial-certification-dates/initial-certification-dates-validators";
import {distinctUntilChanged, takeWhile} from "rxjs/operators";
import {OrderActions} from "../../../../store/actions/order.actions";
import {sanitizeDate} from "../../../../umbrella-common/common-functions";
import {BaseOrderFormService} from "../../common/base-order-form.service";
import {formatDate} from "../../../../store/reducers/order.reducer";
import {Moment} from "moment";

@Injectable()
export class AuditDatesFormService extends FormService implements BaseOrderFormService {

	readonly FORM_CONTROL_ERROR_MSG_MAP: { [validationKey: string]: ValidationMsg[] } = {
		preAuditBeforeStage1: [{msg: 'validations.additional-questions.audit-dates.pre-audit-before-stage-1'}],
		preAuditBeforeStage2: [{msg: 'validations.additional-questions.audit-dates.pre-audit-before-stage-2'}],
		preAuditBeforeDesiredNextAuditDate: [{msg: 'validations.additional-questions.audit-dates.pre-audit-before-next-audit'}],
		preAuditInPast: [{msg: "validations.additional-questions.audit-dates.date-in-past"}],
		stage1BeforeStage2: [{msg: 'validations.additional-questions.audit-dates.stage-1-before-stage-2'}],
		stage1AfterPreAudit: [{msg: 'validations.additional-questions.audit-dates.after-pre-audit'}],
		stage1InPast: [{msg: "validations.additional-questions.audit-dates.date-in-past"}],
		stage2AfterPreAudit: [{msg: 'validations.additional-questions.audit-dates.after-pre-audit'}],
		stage2AfterStage1: [{msg: 'validations.additional-questions.audit-dates.stage-2-after-stage-1'}],
		stage2InPast: [{msg: "validations.additional-questions.audit-dates.date-in-past"}],
		nbDatepickerParse: [{msg: 'validations.additional-questions.audit-dates.incorrect-date-format'}],
		desiredNextAuditDateInPast: [{msg: "validations.additional-questions.audit-dates.date-in-past"}]
	};

	private readonly DEVICE_TYPE = deviceType;
	private controlNamesInOrder: string[] = [];
	private subFormsProcessTypes: { [formKey: string]: ProcessType } = {};

	constructor(translate: TranslateService, private fb: FormBuilder, private store: Store<AppState>) {
		super(translate);
	}

	fillOrder(order: Order): Order {
		this.getAllFormKeys().forEach(formKey => this.fillProduct(order, formKey));
		return order;
	}

	getControlNamesInOrder(): string[] {
		return this.controlNamesInOrder;
	}

	override copyAnswers(from: StandardName, to: StandardName) {
		if (this.isCopyingFromNonInitialToInitial(from, to)) {
			this.tryCopy("desiredNextAuditDate", from, 'stage2AuditDate', to);
		} else if (this.isCopyingFromInitialToNonInitial(from, to)) {
			this.tryCopy("stage2AuditDate", from, "desiredNextAuditDate", to);
		} else {
			super.copyAnswers(from, to);
		}
	}

	override destroy() {
		super.destroy();
		this.controlNamesInOrder = [];
		this.subFormsProcessTypes = {};
	}

	protected buildFormFor(formKey: string, [processType, product]: [ProcessType, Product]): FormGroup {
		this.subFormsProcessTypes[formKey] = processType;
		return processType == ProcessType.INITIAL_CERTIFICATION ?
			this.buildForInitialCertification(formKey, product) :
			this.buildForNonInitial(formKey, processType, product);
	}

	protected override getFormSpecificErrorMap(): { [p: string]: ValidationMsg[] } {
		return this.FORM_CONTROL_ERROR_MSG_MAP;
	}

	protected tryCopy(srcControlName: string, srcForm: string, dstControlName: string, dstForm: string) {
		const srcControl = this.getFormControl(srcForm, srcControlName);
		const dstControl = this.getFormControl(dstForm, dstControlName);

		if (this.canCopy(srcControl, dstControl)) {
			dstControl.setValue(srcControl.value);
			this.triggerValidationsFor(dstForm, dstControlName);
		}
	}

	private buildForInitialCertification(formKey: string, product: Product) {
		const form = this.fb.group({});

		const preAuditRequestCheckboxControl = this.preAuditDateRequestCheckbox(product);
		const preAuditDateControl = this.preAuditDateControl(product);
		const stage1AuditDateControl = this.stage1AuditDateControl(product);
		const stage2AuditDateControl = this.stage2AuditDateControl(product);

		form.addControl('preAuditRequestCheckbox' + formKey, preAuditRequestCheckboxControl);
		form.addControl('preAuditDate' + formKey, preAuditDateControl);
		form.addControl('stage1AuditDate' + formKey, stage1AuditDateControl);
		form.addControl('stage2AuditDate' + formKey, stage2AuditDateControl);

		preAuditDateControl.setValidators(InitialCertificationDatesValidators.preAuditDateValidator(form, formKey));
		stage1AuditDateControl.setValidators(InitialCertificationDatesValidators.stage1AuditDateValidator(form, formKey));
		stage2AuditDateControl.setValidators(InitialCertificationDatesValidators.stage2AuditDateValidator(form, formKey));
		this.initControlNamesInOrder(formKey, 'preAuditRequestCheckbox', 'preAuditDate', 'stage1AuditDate', 'stage2AuditDate');

		return form;
	}

	private preAuditDateRequestCheckbox(product: Product) {
		const control: FormControl = this.fb.control(product.additionalQuestions.initialCertificationQuestions.preAuditRequested);
		control.valueChanges.pipe(takeWhile(() => this.isSubFormInitialized(product.standard.name)), distinctUntilChanged()).subscribe(value => {
				if (value == false) {
					this.updateFormControl('preAuditDate', null, product.standard.name);
				}
				this.store.dispatch(OrderActions.SET_PRE_AUDIT_REQUESTED({standardName: product.standard.name, value}));
			}
		);
		return control;
	}

	private preAuditDateControl(product: Product) {
		const preAuditDateControl = this.fb.control(this.getDate(product.additionalQuestions.initialCertificationQuestions.desiredPreAuditDate));
		preAuditDateControl.valueChanges.pipe(takeWhile(() => this.isSubFormInitialized(product.standard.name)), distinctUntilChanged()).subscribe(date => {
			this.updateDatePickerFormControl(product.standard.name, date, 'preAuditDate');
			this.triggerSubFormValidations(product.standard.name);
			if (date != null) {
				this.updateFormControl('preAuditRequestCheckbox', true, product.standard.name);
			}
			this.store.dispatch(OrderActions.SET_PRE_AUDIT_DATE({standardName: product.standard.name, date: date?.toDate()}));
		});
		return preAuditDateControl;
	}

	private stage1AuditDateControl(product: Product) {
		const stage1AuditDateControl = this.fb.control(this.getDate(product.additionalQuestions.initialCertificationQuestions.desiredStage1AuditDate));
		stage1AuditDateControl.valueChanges.pipe(takeWhile(() => this.isSubFormInitialized(product.standard.name)), distinctUntilChanged()).subscribe(date => {
			this.updateDatePickerFormControl(product.standard.name, date, 'stage1AuditDate');
			this.triggerSubFormValidations(product.standard.name);
			this.store.dispatch(OrderActions.SET_STAGE_1_AUDIT_DATE({standardName: product.standard.name, date: date?.toDate()}));
		});
		return stage1AuditDateControl;
	}

	private stage2AuditDateControl(product: Product) {
		const stage2AuditDateControl = this.fb.control(this.getDate(product.additionalQuestions.desiredNextAuditDate));
		stage2AuditDateControl.valueChanges.pipe(takeWhile(() => this.isSubFormInitialized(product.standard.name)), distinctUntilChanged()).subscribe(date => {
			this.updateDatePickerFormControl(product.standard.name, date, 'stage2AuditDate');
			this.triggerSubFormValidations(product.standard.name);
			this.store.dispatch(OrderActions.SET_NEXT_AUDIT_DATE({standardName: product.standard.name, date: date?.toDate()}));
		});
		return stage2AuditDateControl;
	}

	private validateDesiredNextAuditDate(desiredNextAuditDate: Date): ValidationErrors | null {
		const dateInPast = isInPast(desiredNextAuditDate);

		if (!dateInPast) {
			return null;
		} else {
			return {desiredNextAuditDateInPast: []};
		}
	}

	private buildForNonInitial(formKey: string, processType: ProcessType, product: Product) {
		let validators = [control => this.validateDesiredNextAuditDate(sanitizeDate(control.value))];
		if (processType == ProcessType.RECERTIFICATION) {
			validators.push(Validators.required);
		}

		const desiredNextAuditDate = this.fb.control(this.getDate(product.additionalQuestions.desiredNextAuditDate), validators);
		desiredNextAuditDate.valueChanges.pipe(takeWhile(() => this.isSubFormInitialized(formKey)), distinctUntilChanged()).subscribe((date: Moment) => {
			this.updateDatePickerFormControl(formKey, date, 'desiredNextAuditDate');
			this.triggerSubFormValidations(formKey);
			this.store.dispatch(OrderActions.SET_NEXT_AUDIT_DATE({standardName: formKey as StandardName, date: date?.toDate()}));
		});

		const form = this.fb.group({});
		form.addControl('desiredNextAuditDate' + formKey, desiredNextAuditDate);
		this.initControlNamesInOrder(formKey, 'desiredNextAuditDate');
		return form;
	}

	private getDate(date: Date | string) {
		if (date) {
			return moment(dateWithoutTime(date));
		}

		return null;
	}

	private updateDatePickerFormControl(formKey: string, date, controlName: string) {
		// workaround for table datepicker 'clear' option
		if (date === '') {
			this.updateFormControl(controlName, null, formKey);
		}
	}

	private fillInitialCertificationProduct(product: Product) {
		product.additionalQuestions.desiredNextAuditDate = this.formatAndSanitizeDate(this.getValueOrNull(product.standard.name, 'stage2AuditDate'));
		product.additionalQuestions.initialCertificationQuestions = this.getInitialCertificationQuestions(product.standard.name);
	}

	private getInitialCertificationQuestions(standardName: StandardName): AdditionalInitialCertificationQuestions {
		const preAuditRequest = this.getValueFor('preAuditRequestCheckbox', standardName);
		return {
			preAuditRequested: preAuditRequest != null ? preAuditRequest : false,
			desiredPreAuditDate: this.formatAndSanitizeDate(this.getValueOrNull(standardName, 'preAuditDate')),
			desiredStage1AuditDate: this.formatAndSanitizeDate(this.getValueOrNull(standardName, 'stage1AuditDate'))
		};
	}

	private formatAndSanitizeDate(date: Date): Date {
		const result = formatDate(sanitizeDate(date));
		// @ts-ignore
		return result;

	}

	private fillProduct(order: Order, formKey: string) {
		let product = order.products.find(p => p.standard.name == formKey);
		if (this.subFormsProcessTypes[formKey] == ProcessType.INITIAL_CERTIFICATION) {
			this.fillInitialCertificationProduct(product);
		} else {
			this.fillNonInitialProduct(product);
		}
	}

	private fillNonInitialProduct(product: Product) {
		product.additionalQuestions.desiredNextAuditDate = this.formatAndSanitizeDate(this.getValueOrNull(product.standard.name, 'desiredNextAuditDate'));
	}

	private isCopyingFromNonInitialToInitial(from: StandardName, to: StandardName) {
		return this.subFormsProcessTypes[from] != ProcessType.INITIAL_CERTIFICATION && this.subFormsProcessTypes[to] == ProcessType.INITIAL_CERTIFICATION;
	}

	private isCopyingFromInitialToNonInitial(from: StandardName, to: StandardName) {
		return this.subFormsProcessTypes[from] == ProcessType.INITIAL_CERTIFICATION && this.subFormsProcessTypes[to] != ProcessType.INITIAL_CERTIFICATION;
	}

	private initControlNamesInOrder(formKey: string, ...controlNames: string[]) {
		this.controlNamesInOrder = this.controlNamesInOrder.concat(controlNames.map(name => name + formKey));
	}
}
